// The desktop web app's platform adapter for @battleops/client (contract: packages/client/src/platform.js).
// Same storage keys and IndexedDB names as the Expo web export it replaces, so a browser that was signed in
// stays signed in and keeps anything still waiting to sync.
import { idbDriver, webTabs, electLeader } from '@battleops/localstore';
import { setPlatform } from '@battleops/client/src/platform';
import { initializeApp } from '@battlesuite/sdk/app';
import { getAuth, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, onAuthStateChanged } from '@battlesuite/sdk/auth';
import { getFunctions, httpsCallable } from '@battlesuite/sdk/functions';

const API = import.meta.env.VITE_API_BASE_URL || 'https://api.battlebooks.ca';

const kv = {
  getItem: async (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  setItem: async (k, v) => { localStorage.setItem(k, v); },
  removeItem: async (k) => { localStorage.removeItem(k); },
};

async function putFile(url, localUri, contentType) {
  const blob = localUri instanceof Blob ? localUri : await (await fetch(localUri)).blob();
  const res = await fetch(url, { method: 'PUT', headers: { 'content-type': contentType }, body: blob });
  if (!res.ok) { const e = new Error(`upload HTTP ${res.status}`); e.status = res.status; throw e; }
}

// Sync relay between tabs (only the leader tab runs the engine — packages/client/src/session/boot.js).
function channel(name) {
  if (typeof BroadcastChannel === 'undefined') return { post() {}, subscribe: () => () => {}, close() {} };
  const ch = new BroadcastChannel(`battleops-sync:${name}`);
  const subs = new Set();
  ch.onmessage = (e) => subs.forEach((fn) => { try { fn(e.data); } catch { /* listener */ } });
  return { post: (m) => ch.postMessage(m), subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); }, close: () => ch.close() };
}

// A browser tab keeps syncing while hidden (it may be the leader for the tab you're looking at), so it is
// always 'active'; coming back into view still reports 'active' so the app refreshes and syncs.
const appState = {
  current: () => 'active',
  subscribe: (fn) => {
    const on = () => { if (document.visibilityState === 'visible') fn('active'); };
    document.addEventListener('visibilitychange', on);
    window.addEventListener('focus', on);
    return () => { document.removeEventListener('visibilitychange', on); window.removeEventListener('focus', on); };
  },
};

setPlatform({
  os: 'web',
  appVersion: __APP_VERSION__,
  runtimeVersion: null,
  apiBaseUrl: API,
  kv,
  driver: (name) => idbDriver({ name }),
  sdk: {
    initializeApp,
    initAuth: (app) => getAuth(app),
    getFunctions, httpsCallable, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, onAuthStateChanged,
  },
  net: {
    fetch: async () => navigator.onLine,
    subscribe: (fn) => {
      const up = () => fn(true); const down = () => fn(false);
      window.addEventListener('online', up); window.addEventListener('offline', down);
      fn(navigator.onLine);
      return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
    },
  },
  appState,
  files: { putFile, downloadToCache: async (url) => url },
  media: null,
  background: { armSyncTask: () => {}, followOutbox: () => {} },
  onBeforeReloadLast: (fn) => window.addEventListener('pagehide', () => { fn(); }),
  tabs: { coordinator: (name) => webTabs(name), electLeader, channel },
});
